"use client";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useForgotPassword } from '../context/ForgotPasswordContext'; // adjust path

function ForgotPassword() {
  const navigate = useNavigate();
  const { sendResetLink } = useForgotPassword();

  const [email, setEmail] = React.useState("");
  const [error, setError] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateEmail = (value) => {
    if (!value.trim()) return "Email is required";
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return "";
  };

  const handleInputChange = (e) => {
    setEmail(e.target.value);
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await sendResetLink(email);
      if (response.status === 200) {
        setShowModal(true);
        setEmail("");
      } else {
        setError(response.message || "Something went wrong");
      }
    } catch (err) {
      setError(err.message || "Unable to send reset link");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavigation = (path) => navigate(path);
  const closeModal = () => {
    setShowModal(false);
    navigate("/login");
  };

  const underlineVariants = {
    initial: { scaleX: 0 },
    hover: { scaleX: 1, transition: { duration: 0.3, ease: "easeInOut" } },
    exit: { scaleX: 0, transition: { duration: 0.3, ease: "easeInOut" } }
  };

  const AnimatedLink = ({ children, onClick, className = "" }) => (
    <motion.button
      onClick={onClick}
      className={`relative text-yellow-500 hover:opacity-80 bg-transparent border-none cursor-pointer p-0 outline-none ${className}`}
      whileHover="hover"
      initial="initial"
      animate="initial"
    >
      <span className="relative z-10">{children}</span>
      <motion.span
        className="absolute bottom-0 left-0 w-full h-0.5 bg-yellow-400 origin-center"
        variants={underlineVariants}
      />
    </motion.button>
  );

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="flex flex-col gap-8 p-10 rounded-3xl shadow-lg w-full max-w-md">
          {/* Header */}
          <header className="text-center">
            <h1 className="text-4xl font-bold text-yellow-500">Forgot Password</h1>
            <p className="text-gray-600 mt-2 text-sm">Enter your email to receive reset instructions</p>
          </header>

          {/* Navigation */}
          <div className="flex justify-center gap-2 text-gray-700 text-sm">
            <span>Remembered your password?</span>
            <AnimatedLink onClick={() => handleNavigation("/login")}>Sign in here</AnimatedLink>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-5 w-full" noValidate>
            <div className={`flex flex-col w-full`}>
              <div className={`flex items-center px-4 py-3 border rounded-lg transition-colors focus-within:ring-2 focus-within:ring-yellow-300 ${error ? "border-red-500" : "border-yellow-300"}`}>
                <input
                  type="email"
                  value={email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  className="w-full bg-transparent outline-none text-stone-700 placeholder:text-stone-400"
                  aria-invalid={error ? "true" : "false"}
                />
              </div>
              {error && <span className="text-red-500 text-sm mt-1">{error}</span>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-3 bg-yellow-400 hover:bg-yellow-500 text-white font-semibold rounded-lg shadow transition-all disabled:opacity-50"
            >
              {isSubmitting ? "Sending..." : "Send Reset Link"}
            </button>

            <div className="flex justify-center pt-4">
              <AnimatedLink onClick={() => handleNavigation("/")}>Return to Store</AnimatedLink>
            </div>
          </form>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="bg-white rounded-2xl p-8 max-w-md w-full shadow-lg relative"
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-yellow-100 mb-4">
                <svg
                  className="h-7 w-7 text-yellow-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-yellow-500 mb-2">Reset Link Sent!</h2>
              <p className="text-gray-600 mb-6">
                We've sent a password reset link to your email. Please check your inbox and follow the instructions.
              </p>
              <button
                onClick={closeModal}
                className="w-full px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-white rounded-lg transition-all"
              >
                Sign In Now
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}

export default ForgotPassword;
